// =============================================================================
// clientes/src/lib/sessao.tsx — a sessão do parceiro.
//
// Bem menor que a do sistema, de propósito: sem timer de inatividade (o
// portal não é balcão compartilhado — é o celular do dono da oficina) e sem
// papéis. O que existe é: tem login? tem chapéu de parceiro (meu_cadastro_b2b
// devolve linha)? Sem chapéu, a tela diz para falar com a loja.
// =============================================================================
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { meuCadastro, type MeuCadastro } from './consultas';
import { mensagemDeErro, supabase } from './supabase';

interface Sessao {
  session: Session | null;
  /** null com sessão viva E sem erro = login sem chapéu de parceiro. */
  cadastro: MeuCadastro | null;
  /** Preenchido quando NÃO DEU para perguntar (rede, API fora, schema velho).
   *  Separado de cadastro=null de propósito: "não deu para conferir" não é
   *  "você não tem acesso". Mandar o parceiro ligar para a loja por causa de
   *  um 404 do PostgREST faz a loja procurar defeito no lugar errado — e o
   *  sistema interno já aprendeu essa lição (app/src/lib/sessao.tsx). */
  erro: string | null;
  recarregar: () => Promise<void>;
  carregando: boolean;
  sair: () => Promise<void>;
}

const Contexto = createContext<Sessao | null>(null);

export function SessaoProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [cadastro, setCadastro] = useState<MeuCadastro | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;

    async function carregarCadastro(s: Session | null) {
      if (!s) { if (vivo) { setCadastro(null); setErro(null); } return; }
      try {
        const c = await meuCadastro();
        if (vivo) { setCadastro(c); setErro(null); }
      } catch (e) {
        // A consulta FALHOU — não sabemos se ele tem acesso. Guardamos o
        // motivo e a tela oferece "tentar de novo", em vez de acusar o
        // parceiro de não ter cadastro.
        if (vivo) { setCadastro(null); setErro(mensagemDeErro(e)); }
      }
    }

    supabase.auth.getSession().then(async ({ data }) => {
      if (!vivo) return;
      setSession(data.session);
      await carregarCadastro(data.session);
      if (vivo) setCarregando(false);
    });

    const { data: escuta } = supabase.auth.onAuthStateChange((_evento, s) => {
      setSession(s);
      void carregarCadastro(s);
    });

    return () => { vivo = false; escuta.subscription.unsubscribe(); };
  }, [tentativa]);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setTentativa((n) => n + 1);
  }, []);

  const sair = useCallback(async () => {
    // scope local: derrubar só ESTE aparelho (a lição do sistema).
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    queryClient.clear();
    setSession(null);
    setCadastro(null);
  }, [queryClient]);

  const valor = useMemo(
    () => ({ session, cadastro, erro, recarregar, carregando, sair }),
    [session, cadastro, erro, recarregar, carregando, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSessao fora do SessaoProvider');
  return s;
}
